<template>
  <div
    :class="[authenticated ? 'min-h-[405.6px]' : 'min-h-[315.6px]']"
    class="shadow-pm relative mx-auto flex h-full w-full max-w-[324px] flex-col justify-between gap-y-8 rounded-lg bg-white p-6"
  >
    <CloseLockIcon
      v-if="!isMember"
      class="text-p-900 absolute right-4 top-4"
      data-testid="service-card-item-lock"
    />
    <CheckSubscriptionIcon
      v-else
      class="text-p-900 absolute right-4 top-4 h-5 w-5"
      data-testid="service-card-item-check"
    />
    <div class="flex w-full flex-col gap-y-3">
      <!-- icon -->
      <div
        class="icon-container flex h-[88px] w-[88px] items-center justify-center rounded-[32px]"
        data-testid="service-card-item-icon"
      >
        <Icon :name="serviceIcon" class="path-stroke h-[37px] w-[37px]" />
      </div>
      <!-- title -->
      <h3
        class="text-xl font-bold md:text-base"
        :class="[comingSoonBackgroundColor]"
      >
        {{ serviceTitle }}
      </h3>
      <div
        v-if="serviceMenu.length && !service.isComingSoon"
        class="flex w-full flex-col gap-y-2"
        data-testid="service-card-item-menu"
      >
        <CommonLinkWithAnimation
          v-for="item in serviceMenu"
          :key="item.membership"
          class="whitespace-nowrap"
          :url="getCorrectUrl(item.to)"
          :text="item.name"
        />
        <CommonLinkWithAnimation
          text="Contact Us"
          data-testid="service-card-contact-button"
          @click="handleContact()"
        />
      </div>
    </div>
    <CommonButtonDownload
      v-if="Object.keys(brochureFile).length"
      :class="{ 'mt-11': !serviceMenu.length }"
      text="Brochure"
      :download-file="brochureFile"
      variant="tertiary"
      data-testid="service-card-item-download"
    />
    <CommonButton
      v-if="service.isComingSoon"
      variant="primary"
      disabled
      :show-spinner="false"
    >
      Coming Soon
    </CommonButton>
    <CommonButtonDownload
      v-if="isMethodologyActive && isButtonDownload"
      :class="{ 'mt-11': !serviceMenu.length }"
      text="Methodology"
      :download-file="methodologyFile"
      variant="tertiary"
    />
    <CommonButtonLink
      v-if="isMethodologyActive && !isButtonDownload"
      :class="{ 'mt-11': !serviceMenu.length }"
      text="Methodology"
      :route="methodologyUrl"
    />
  </div>
</template>

<script setup lang="ts">
import Icon from '~/components/common/Icon.vue'
import CommonButtonDownload from '~/components/shared/common/ButtonDownload.vue'
import CommonButtonLink from '~/components/shared/common/ButtonLink.vue'
import CommonLinkWithAnimation from '~/components/shared/common/LinkWithAnimation.vue'
import CloseLockIcon from '~/assets/icons/close-lock.svg'
import CheckSubscriptionIcon from '~/assets/icons/check-subscribe.svg'
import { getServices } from '~/constants/services'
import { useMembershipsStore } from '~/stores/memberships'
import { useModalStore } from '~/stores/modal'
import useAuthStore from '~/stores/auth'
import type { TGeneral } from '~/src/types/common'

const props = defineProps<{
  service: {
    slug: string
    hasMarketingPage?: boolean
    name: string
    icon: string
    menu: TGeneral[]
    methodology: TGeneral
    brochure: TGeneral
    isComingSoon: boolean
    membership: string
    initialFields: TGeneral
  }
}>()

const { $config, $rudderstack } = useNuxtApp()

const authStore = useAuthStore()
const membershipsStore = useMembershipsStore()
const modalStore = useModalStore()

const isMember = computed(() =>
  membershipsStore.getSubscription(props.service.membership),
)
const serviceTitle = computed(() => props.service.name ?? '')
const serviceIcon = computed(() => `sidebar/${props.service.icon ?? 'Lithium'}`)
const serviceMenu = computed(() => props.service.menu ?? [])
const isButtonDownload = computed(
  () => props.service.methodology?.type === 'file',
)
const isMethodologyActive = computed(() => !!props.service.methodology?.active)

const methodologyFile = computed(() => {
  if (!isMethodologyActive.value) return {}
  return props.service.methodology?.file ?? {}
})

const methodologyUrl = computed(() => props.service.methodology?.data || '')
const brochureFile = computed(() => {
  if (
    !props.service.brochure ||
    Object.keys(props.service.brochure).length === 0
  )
    return {}

  const file = { ...props.service.brochure }
  file.ID = $config.public[file.EnvKey]
  return file
})
const comingSoonBackgroundColor = computed(() =>
  props.service.isComingSoon ? 'text-p-em-disabled' : '',
)
const authenticated = computed(() => authStore.authenticated)

const handleContact = () => {
  openModal()
}

const openModal = () => {
  $rudderstack.trackContentGatedInteraction()
  modalStore.setInitialFields(getServices(props.service.initialFields))
  modalStore.open('contact')
}

const getCorrectUrl = (url) => {
  if (isMember.value) return url
  if (!props.service.hasMarketingPage) return url
  if (!url.includes('/data-reports/forecasts')) return url
  return `/${props.service.slug}`
}
</script>

<style scoped>
.path-stroke > path {
  @apply text-s-700;
}
</style>
